import {
  decomposeFamilyNameToKeys,
  rankLearnedAutoFillPatterns,
  type LearnedAutoFillPatternV1,
  type LearnedAutoFillValuesV1,
} from '../../../shared/lib/quickFill/learnedAutoFillLibrary';
import { autoFillFromFamilyName } from '../../../shared/lib/utils/autoFillService';

export type QuickFillKeyLibraryMatchSource = 'idle' | 'built-in' | 'local' | 'none';

export interface QuickFillKeyLibraryMatchDiagnostic {
  normalizedKeys: string[];
  source: QuickFillKeyLibraryMatchSource;
  values?: LearnedAutoFillValuesV1;
  matchedPatternId?: string;
  matchedKeywords: string[];
  learnedCandidates: LearnedAutoFillPatternV1[];
  builtInTakesPrecedence: boolean;
  hasAmbiguousLearnedMappings: boolean;
}

const getValuesSignature = (entry: LearnedAutoFillPatternV1): string =>
  JSON.stringify(entry.values);

function hasAmbiguousLearnedMappings(
  candidates: readonly LearnedAutoFillPatternV1[]
): boolean {
  const specificity = candidates[0]?.requiredKeys.length;
  if (specificity === undefined) {
    return false;
  }

  const equallySpecific = candidates.filter(
    (entry) => entry.requiredKeys.length === specificity
  );
  return new Set(equallySpecific.map(getValuesSignature)).size > 1;
}

export function buildQuickFillKeyLibraryMatchDiagnostic(
  familyName: string,
  entries: readonly LearnedAutoFillPatternV1[]
): QuickFillKeyLibraryMatchDiagnostic {
  const normalizedKeys = decomposeFamilyNameToKeys(familyName);
  const learnedCandidates = familyName.trim()
    ? rankLearnedAutoFillPatterns(familyName, entries)
    : [];
  const builtIn = familyName.trim()
    ? autoFillFromFamilyName(familyName, { includeLearned: false })
    : null;
  const builtInTakesPrecedence = Boolean(builtIn && builtIn.confidence > 0);
  const hasAmbiguity = hasAmbiguousLearnedMappings(learnedCandidates);

  if (!familyName.trim()) {
    return {
      normalizedKeys,
      source: 'idle',
      matchedKeywords: [],
      learnedCandidates,
      builtInTakesPrecedence: false,
      hasAmbiguousLearnedMappings: false,
    };
  }

  if (builtInTakesPrecedence && builtIn) {
    return {
      normalizedKeys,
      source: 'built-in',
      values: {
        objectType: builtIn.objectType ?? '',
        skey: builtIn.skey ?? '',
        ...(builtIn.end1 ? { end1: builtIn.end1 } : {}),
        ...(builtIn.end2 ? { end2: builtIn.end2 } : {}),
        ...(builtIn.end3 ? { end3: builtIn.end3 } : {}),
        ...(builtIn.end4 ? { end4: builtIn.end4 } : {}),
      },
      matchedPatternId: builtIn.matchedPatternId,
      matchedKeywords: builtIn.matchedKeywords,
      learnedCandidates,
      builtInTakesPrecedence,
      hasAmbiguousLearnedMappings: hasAmbiguity,
    };
  }

  const winner = learnedCandidates[0];
  if (winner) {
    return {
      normalizedKeys,
      source: 'local',
      values: winner.values,
      matchedPatternId: `learned:${winner.id}`,
      matchedKeywords: [...winner.requiredKeys],
      learnedCandidates,
      builtInTakesPrecedence: false,
      hasAmbiguousLearnedMappings: hasAmbiguity,
    };
  }

  return {
    normalizedKeys,
    source: 'none',
    matchedKeywords: [],
    learnedCandidates,
    builtInTakesPrecedence: false,
    hasAmbiguousLearnedMappings: false,
  };
}
